import { DeliveryCountry, SupportedLanguages, StringStringMap } from '@as-react/common-types';

export const getIn = (object: Record<string, unknown> | string, path: string) =>
  path.split('.').reduce((previousValue, currentValue) => previousValue?.[currentValue], object);

export const isObject = (value: unknown) => typeof value === 'object' && value !== null && !Array.isArray(value);

export const isEmptyObject = (obj: Record<string, unknown>) =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

export const mergeObjectsFromArray = <T = StringStringMap>(arrayOfObjects: Array<T>) =>
  arrayOfObjects.reduce((acc, curr) => ({ ...acc, ...curr }), {} as T);

export const mergeObjectDeep = (...objects) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValueObject = (obj: any): obj is Record<string, unknown> => obj && typeof obj === 'object';

  return objects.reduce((prev, obj) => {
    Object.keys(obj).forEach(key => {
      const pVal = prev[key];
      const oVal = obj[key];

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        prev[key] = pVal.concat(...oVal);
      } else if (isValueObject(pVal) && isValueObject(oVal)) {
        prev[key] = mergeObjectDeep(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {});
};

export const isEqual = function (value: unknown, other: unknown): boolean {
  // Get the value type
  const type = Object.prototype.toString.call(value);

  // If the two objects are not the same type, return false
  if (type !== Object.prototype.toString.call(other)) return false;

  // If items are not an object or array, return false
  if (type !== '[object Array]' && type !== '[object Object]') return false;

  // Compare the length of the two items
  const valueLen = type === '[object Array]' ? (value as Array<unknown>).length : Object.keys(value as object).length;
  const otherLen = type === '[object Array]' ? (other as Array<unknown>).length : Object.keys(other as object).length;
  if (valueLen !== otherLen) return false;

  // Compare two items
  const compare = (item1: unknown, item2: unknown): boolean => {
    const itemType = Object.prototype.toString.call(item1);

    if (itemType === '[object Array]' || itemType === '[object Object]') {
      if (!isEqual(item1, item2)) {
        return false;
      }
    } else {
      if (itemType !== Object.prototype.toString.call(item2)) {
        return false;
      }
      if (itemType === '[object Function]') {
        if (
          (item1 as (...args: Array<unknown>) => unknown).toString() !==
          (item2 as (...args: Array<unknown>) => unknown).toString()
        )
          return false;
      } else {
        if (item1 !== item2) return false;
      }
    }
    return true;
  };

  // Compare properties
  if (type === '[object Array]') {
    for (let i = 0; i < valueLen; i++) {
      if (!compare((value as Array<unknown>)[i], (other as Array<unknown>)[i])) return false;
    }
  } else {
    for (const key in value as object) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        if (!compare((value as Record<string, unknown>)[key], (other as Record<string, unknown>)[key])) return false;
      }
    }
  }

  return true;
};

export const isNullOrUndefined = <T>(obj: null | undefined | T): obj is null | undefined => {
  return obj === null || obj === undefined || typeof obj === 'undefined';
};

export const filterDataFromObjectByKey = <
  Data,
  T extends Record<string, Data> = Record<string, Data>,
  Key extends keyof T & string = (keyof T & string) | Lowercase<keyof T & string>,
>(
  keyedObject: T,
  keyToFilter: Key
) => {
  if (keyedObject && keyToFilter) {
    return keyedObject[keyToFilter.toUpperCase()];
  }
  return null;
};

export const uniqueId = (prefix = '', suffix = '', length = 8) => {
  const randomChars = Math.random().toString(36).substring(2, 4);
  const randomNumbers = Math.random().toString().slice(2, length);
  return `${prefix}${randomChars}${randomNumbers}${suffix}`;
};

export const sortAlphabetically = <T extends Record<string, unknown> | string>(
  array: Array<T>,
  key?: string,
  compareOptions: Intl.CollatorOptions = {
    sensitivity: 'base',
    numeric: true,
  }
) =>
  [...array].sort((a, b) => {
    const compareA = key ? getIn(a, key) : a;
    const compareB = key ? getIn(b, key) : b;
    return compareA.toString().localeCompare(compareB.toString(), undefined, compareOptions);
  });

export const sortCountriesAlphabetically = (array: Array<DeliveryCountry>, language?: string) =>
  [...array].sort((a, b) => {
    const compareA = a.countryTranslations[language?.toUpperCase() as SupportedLanguages]!.countryDescription;
    const compareB = b.countryTranslations[language?.toUpperCase() as SupportedLanguages]!.countryDescription;
    return compareA.localeCompare(compareB, undefined, {
      sensitivity: 'base',
      numeric: true,
    });
  });

export class Poller {
  public active = false;

  constructor(
    private fn: () => boolean | string | Promise<string | undefined> | Promise<boolean | undefined>,
    private interval: number
  ) {}

  public async run() {
    this.active = true;
    const wait = () => new Promise(resolve => setTimeout(resolve, this.interval));
    let result = await this.fn();
    while (!result && this.active) {
      await wait();
      if (!this.active) {
        break;
      }
      result = await this.fn();
    }
    return result;
  }

  public end() {
    this.active = false;
  }
}

export function cloneDeep<T>(source: T): T {
  if (Array.isArray(source)) {
    return source.map(item => cloneDeep(item)) as unknown as T;
  } else if (source instanceof Date) {
    return new Date(source.getTime()) as unknown as T;
  } else if (source && typeof source === 'object') {
    return Object.getOwnPropertyNames(source).reduce(
      (acc, prop) => {
        Object.defineProperty(acc, prop, Object.getOwnPropertyDescriptor(source, prop)!);
        acc[prop as keyof typeof acc] = cloneDeep(source[prop as keyof typeof source]);
        return acc;
      },
      Object.create(Object.getPrototypeOf(source))
    );
  } else {
    return source;
  }
}

export const handleAllSettledResult = <T, F>({
  result,
  onError,
  fallbackValue = null,
}: {
  result: PromiseSettledResult<T>;
  fallbackValue?: F | null;
  onError?: (reason: PromiseRejectedResult['reason']) => void;
}) => {
  if (result.status === 'fulfilled') {
    return result.value || fallbackValue;
  } else {
    onError?.(result.reason);
    return fallbackValue;
  }
};

type PendingPromise<R> = { resolve: (value: R) => void; reject: (reason?: unknown) => void; promise: Promise<R> };
export const debouncedPromise = <A extends Array<unknown>, R>(fn: (...args: A) => Promise<R>, delay: number) => {
  let debounceTimer: ReturnType<typeof setTimeout> | null;
  let pendingPromise: PendingPromise<R> | null = null;
  let lastArgs: A;

  return (...args: A): Promise<R> => {
    lastArgs = args;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (!pendingPromise) {
      pendingPromise = {} as PendingPromise<R>;
      pendingPromise.promise = new Promise<R>((resolve, reject) => {
        pendingPromise!.resolve = resolve;
        pendingPromise!.reject = reject;
      });
    }
    debounceTimer = setTimeout(async () => {
      try {
        const result = await fn(...lastArgs);
        pendingPromise?.resolve(result);
      } catch (err) {
        pendingPromise?.reject(err);
      }
      pendingPromise = null;
    }, delay);
    return pendingPromise.promise;
  };
};
